import React from 'react'
import { Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { MedicationTableData } from '../types'
import { generateDatesArray, MEDICATION_ACTIONS } from './utils'
import OutcomePopover from './popovers/OutcomePopover'

type MedicationsTableProps = {
  startDate: Dayjs
  endDate: Dayjs
  type: 'scheduled' | 'prn'
  residentFirstName?: string
  onCellClick?: (record: any, dateKey: string) => void
  onMedicationNameClick?: (medicationId: string) => void
  data?: any[]
}

const MedicationsTable = ({
  startDate,
  endDate,
  type,
  residentFirstName = '',
  onCellClick,
  onMedicationNameClick,
  data
}: MedicationsTableProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { id: residentId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const showHourColumn = type === 'scheduled'

  const handleMedicationNameClick = (medicationId: string) => {
    if (onMedicationNameClick) {
      onMedicationNameClick(medicationId)
    } else if (residentId) {
      navigate(`/residents/${residentId}/emar/${medicationId}`)
    }
  }

  const datesArray = generateDatesArray(
    startDate.startOf('day'),
    endDate.endOf('day')
  )

  const medicationRowSpans = React.useMemo(() => {
    if (!data || data.length === 0) {
      return {
        rowSpans: new Map<string, number>(),
        firstRowKeys: new Set<string>()
      }
    }
    const rowSpans = new Map<string, number>()
    const firstRowKeys = new Set<string>()
    const medicationGroups: Record<
      string,
      { count: number; firstKey: string }
    > = {}

    data.forEach((row) => {
      const medicationId = row.residentMedicationId
      if (!medicationGroups[medicationId]) {
        medicationGroups[medicationId] = { count: 0, firstKey: row.key }
        firstRowKeys.add(row.key)
      }
      medicationGroups[medicationId].count += 1
    })

    Object.entries(medicationGroups).forEach(([medicationId, { count }]) => {
      rowSpans.set(medicationId, count)
    })

    return { rowSpans, firstRowKeys }
  }, [data])

  const generateDateColumns = (): ColumnsType<any> => {
    return datesArray.map(({ month, days }) => ({
      title: (
        <YearContainer>
          <SecondaryCellText level={2}>
            {dayjs(month, 'MM/YY').format('MMMM YYYY')}
          </SecondaryCellText>
        </YearContainer>
      ),
      children: days.map((date) => {
        const dateKey = dayjs(date, 'DD/MM/YYYY').format('DD-MM-YYYY')
        return {
          dataIndex: dateKey,
          key: dateKey,
          title: (
            <CommonCell>
              <CellText>{dayjs(date, 'DD/MM/YYYY').format('D')}</CellText>
              <SecondaryCellText level={2}>
                {dayjs(date, 'DD/MM/YYYY').format('ddd')}
              </SecondaryCellText>
            </CommonCell>
          ),
          align: 'center' as const,
          onCell: (record: any) => {
            const cellData = record[dateKey]
            if (
              !cellData ||
              !cellData.outcomes ||
              cellData.outcomes.length === 0
            ) {
              return {
                style: {
                  backgroundColor: 'white',
                  padding: 0
                }
              }
            }

            const outcomes = cellData.outcomes
            const lastOutcome = outcomes[outcomes.length - 1]
            const action = MEDICATION_ACTIONS.find(
              (a) => a.translationKey === lastOutcome.action
            )

            if (!action) {
              return { style: { backgroundColor: 'white', padding: 0 } }
            }

            let backgroundColor = 'white'
            const translationKey = action.translationKey

            if (translationKey === 'ADMINISTERED') {
              backgroundColor = type === 'prn' ? 'white' : '#37A5980D'
            } else if (
              translationKey === 'NOT_SCHEDULED_OR_NOT_NEEDED' ||
              translationKey === 'DISCONTINUED'
            ) {
              backgroundColor = '#F5F5F5'
            } else if (translationKey === 'MISSED') {
              backgroundColor = '#E01E4E0D'
            } else if (translationKey === 'PAUSED') {
              backgroundColor = 'white'
            } else {
              backgroundColor = 'white'
            }

            return {
              style: {
                backgroundColor,
                padding: 0
              }
            }
          },
          render: (cellData: any, record: any, index: number) => {
            if (
              !cellData ||
              !cellData.outcomes ||
              cellData.outcomes.length === 0
            ) {
              return null
            }

            const outcomes = cellData.outcomes
            const firstOutcome = outcomes[0]
            const action = MEDICATION_ACTIONS.find(
              (a) => a.translationKey === firstOutcome.action
            )

            if (!action) {
              return null
            }

            const translationKey = action.translationKey
            const showIcon = action.icon
            const showCode = action.code

            const cellContent = (
              <DateCellContainer>
                {showIcon ? (
                  <OutcomeIcon
                    $isMissed={translationKey === 'MISSED'}
                    $isPaused={translationKey === 'PAUSED'}
                  >
                    {action.icon}
                  </OutcomeIcon>
                ) : showCode ? (
                  <OutcomeCode
                    $isMissed={translationKey === 'MISSED'}
                    $isPaused={translationKey === 'PAUSED'}
                  >
                    {action.code}
                  </OutcomeCode>
                ) : null}
                {outcomes.length > 1 && (
                  <RecordCount>+{outcomes.length - 1}</RecordCount>
                )}
              </DateCellContainer>
            )

            const hasPopover = [
              'ADMINISTERED',
              'REFUSED',
              'PAUSED',
              'DISCONTINUED',
              'NOT_SCHEDULED_OR_NOT_NEEDED'
            ].includes(translationKey)

            if (hasPopover) {
              const handleClick = (e: React.MouseEvent) => {
                if (translationKey === 'ADMINISTERED' && onCellClick) {
                  e.stopPropagation()
                  onCellClick(record, dateKey)
                }
              }

              return (
                <OutcomePopover
                  outcome={firstOutcome}
                  record={record}
                  type={type}
                  residentFirstName={residentFirstName}
                >
                  <ClickableCell onClick={handleClick}>
                    {cellContent}
                  </ClickableCell>
                </OutcomePopover>
              )
            }

            return cellContent
          }
        }
      })
    }))
  }

  const baseColumns: ColumnsType<any> = [
    {
      title: ' ',
      children: [
        {
          title: (
            <MedicationHeaderCell>
              <CellText>{t('MEDICATION')}</CellText>
            </MedicationHeaderCell>
          ),
          dataIndex: 'medication',
          key: 'medication',
          fixed: 'left' as const,
          align: 'left' as const,
          render: (medication: MedicationTableData, record: any) => {
            const medicationId = record.residentMedicationId
            const rowSpan = medicationRowSpans.rowSpans.get(medicationId)
            const isFirstRow = medicationRowSpans.firstRowKeys.has(record.key)

            const cellContent = isFirstRow ? (
              <MedicationCell>
                {onMedicationNameClick || residentId ? (
                  <ClickableMedicationContainer
                    onClick={() => handleMedicationNameClick(medicationId)}
                  >
                    <ClickableMedicationText level={1.5}>
                      {medication.name}
                    </ClickableMedicationText>
                  </ClickableMedicationContainer>
                ) : (
                  <MedicationText level={1.5}>{medication.name}</MedicationText>
                )}
                <LabelValueContainer>
                  <MedicationFieldLabel level={2}>
                    {t('CURRENT_STOCK')}:
                  </MedicationFieldLabel>
                  <MedicationFieldValue
                    level={2}
                    $lowStock={medication.lowStock}
                    $outOfStock={medication.outOfStock}
                  >
                    {medication.currentStock} {medication.stockUnit}
                  </MedicationFieldValue>
                </LabelValueContainer>
                <LabelValueContainer>
                  <MedicationFieldLabel level={2}>
                    {t('START_DATE')}:
                  </MedicationFieldLabel>
                  <MedicationFieldValue level={2}>
                    {medication.startDate}
                  </MedicationFieldValue>
                </LabelValueContainer>
                <LabelValueContainer>
                  <MedicationFieldLabel level={2}>
                    {t('END_DATE')}:
                  </MedicationFieldLabel>
                  <MedicationFieldValue level={2}>
                    {medication.endDate || t('NOT_SET')}
                  </MedicationFieldValue>
                </LabelValueContainer>
                {medication.isControlledDrug && (
                  <Tag color="red">{t('CD')}</Tag>
                )}
              </MedicationCell>
            ) : null

            return {
              children: cellContent,
              props: {
                rowSpan: isFirstRow && rowSpan ? rowSpan : 0
              }
            }
          }
        },
        ...(showHourColumn
          ? [
              {
                title: (
                  <CommonCell>
                    <CellText>{t('HOUR')}</CellText>
                  </CommonCell>
                ),
                dataIndex: 'hour',
                key: 'hour',
                fixed: 'left' as const,
                align: 'center' as const,
                render: (text: string) => <CellText>{text}</CellText>
              }
            ]
          : []),
        {
          title: (
            <CommonCell>
              <CellText>{t('DOSE')}</CellText>
            </CommonCell>
          ),
          dataIndex: 'dose',
          key: 'dose',
          fixed: 'left' as const,
          align: 'center' as const,
          render: (text: string) => <CellText>{text}</CellText>
        }
      ]
    }
  ]

  const columns: ColumnsType<any> = [...baseColumns, ...generateDateColumns()]

  return (
    <StyledTable
      bordered
      columns={columns}
      dataSource={data || []}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  )
}

const YearContainer = styled.div`
  display: flex;
`

const SecondaryCellText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 500;
`

const CellText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 500;
`

const MedicationText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 700;
`

const ClickableMedicationText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 700;
`

const ClickableMedicationContainer = styled.div`
  cursor: pointer;
  display: inline-block;

  &:hover ${ClickableMedicationText} {
    text-decoration: underline;
  }
`

const MedicationFieldLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const MedicationFieldValue = styled(Text)<{
  $lowStock?: boolean
  $outOfStock?: boolean
}>`
  color: ${({ theme, $outOfStock, $lowStock }) =>
    $outOfStock
      ? theme.color.status.danger
      : $lowStock
        ? theme.color.status.warning
        : theme.color.neutral.color4};
  font-weight: 500;
`

const LabelValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const MedicationHeaderCell = styled.div`
  width: 200px;
`

const MedicationCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 200px;
  align-items: flex-start;
`

const CommonCell = styled.div`
  display: flex;
  flex-direction: column;
  width: 56px;
  align-items: center;
`

const DateCellContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  width: 100%;
  height: 100%;
`

const OutcomeIcon = styled.div<{ $isMissed: boolean; $isPaused: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isMissed, $isPaused, theme }) =>
    $isMissed
      ? theme.color.status.danger
      : $isPaused
        ? theme.color.neutral.color5
        : theme.color.neutral.color5};
  svg {
    width: 16px;
    height: 16px;
  }
`

const OutcomeCode = styled(Text)<{ $isMissed: boolean; $isPaused: boolean }>`
  color: ${({ $isMissed, $isPaused, theme }) =>
    $isMissed
      ? theme.color.status.danger
      : $isPaused
        ? theme.color.neutral.color5
        : theme.color.neutral.color3};
  font-weight: 600;
  font-size: 14px;
`

const RecordCount = styled(Text)`
  font-size: 10px;
  color: ${({ theme }) => theme.color.neutral.color5};
  margin-top: 2px;
`

const ClickableCell = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
`

const ClickableStockValue = styled(MedicationFieldValue)`
  cursor: pointer;
  color: ${({ theme }) => theme.color.category.clinicalCare};

  &:hover {
    text-decoration: underline;
  }
`

const StyledTable = styled(Table)`
  .ant-table-thead > tr:first-child > th {
    padding: 8px;
    height: auto;
  }

  .ant-table-thead > tr:nth-child(2) > th {
    padding: 4px 8px;
    height: auto;
  }
`

export default MedicationsTable
